/**
 * Alerts — the operator's active-problem queue for the current tenant. Lists open
 * alerts (room-offline = critical, heartbeat-stale = warning) from
 * `GET /api/health/alerts`, each acknowledgeable so it stops nagging. "Re-check now"
 * reconciles the tenant immediately (so a recovered room's alert auto-resolves
 * without waiting for monitor's cron). Tenant-scoped exactly like the rooms list.
 */

import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Button from '@8x8/oxygen-button';
import Tag from '@8x8/oxygen-tag';
import { CheckCircleIcon, CheckCircleSolidIcon, RefreshIcon } from '@8x8/oxygen-icon';
import type { Alert } from '@spaces/contracts';
import type { SessionState } from '../api';
import { ackAlert, listAlerts, rescan } from '../api';
import { navigate } from '../router';
import { useToast } from '../toast';
import { fmtRelative } from '../ui/format';
import { EmptyState, Muted, Notice, PageTitle, SkeletonRows } from '../ui/primitives';

const Page = styled.div`
    padding: 16px 24px 32px;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 18px;
`;

const Card = styled.div`
    background: ${p => p.theme.ui06};
    border: 1px solid ${p => p.theme.ui01};
    border-radius: 8px;
    overflow: hidden;
`;

const Toolbar = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border-bottom: 1px solid ${p => p.theme.ui01};
`;

const Row = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 13px 16px;
    border-bottom: 1px solid ${p => p.theme.ui01};

    &:last-child {
        border-bottom: none;
    }
`;

const Body = styled.div`
    flex: 1;
    min-width: 0;
`;

const Msg = styled.div`
    font-size: 13.5px;
    color: ${p => p.theme.textColor01};
`;

const Sub = styled.div`
    font-size: 12px;
    color: ${p => p.theme.textColor05};
    margin-top: 3px;
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
`;

const RoomLink = styled.button`
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 0;
    color: ${p => p.theme.action08};
    font-size: 12px;
    font-family: ui-monospace, Menlo, Consolas, monospace;

    &:hover {
        text-decoration: underline;
    }
`;

const Check = styled.label`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12.5px;
    color: ${p => p.theme.textColor02};
    cursor: pointer;
    margin-left: auto;
`;

function SeverityTag({ severity }: { severity: Alert['severity'] }): JSX.Element {
    const variant = severity === 'critical' ? 'red' : severity === 'warning' ? 'yellow' : 'blue';
    const label = severity === 'critical' ? 'Critical' : severity === 'warning' ? 'Warning' : 'Info';

    return <Tag variant={variant}>{label}</Tag>;
}

export function Alerts({ session }: { session: SessionState }): JSX.Element {
    // Tenant is the operator's own (their 8x8 token's cid).
    const tenant = session.tenant ?? null;

    if (!tenant) {
        return <Page><Notice kind="warning">Your token carries no tenant — sign in again.</Notice></Page>;
    }

    return <AlertsForTenant tenant={tenant} />;
}

function AlertsForTenant({ tenant }: { tenant: string }): JSX.Element {
    const toast = useToast();
    const [alerts, setAlerts] = useState<Alert[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [includeAck, setIncludeAck] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [busy, setBusy] = useState<string | null>(null);
    const [rescanning, setRescanning] = useState(false);
    const reqId = useRef(0);

    useEffect(() => {
        const id = ++reqId.current;

        setAlerts(null);
        setError(null);
        void listAlerts(tenant, includeAck).then(result => {
            if (id !== reqId.current) {
                return;
            }
            if (result.ok) {
                setAlerts(result.data.alerts);
            } else {
                setError(result.message);
            }
        });
    }, [tenant, includeAck, reloadKey]);

    const acknowledge = async (alert: Alert): Promise<void> => {
        setBusy(alert.id);
        const result = await ackAlert(tenant, alert.id);

        setBusy(null);
        if (!result.ok) {
            toast.notify('error', `Couldn't acknowledge: ${result.message}`);

            return;
        }
        toast.notify('success', 'Alert acknowledged.');
        setReloadKey(k => k + 1);
    };

    const recheck = async (): Promise<void> => {
        setRescanning(true);
        const result = await rescan(tenant);

        setRescanning(false);
        if (!result.ok) {
            toast.notify('error', `Re-check failed: ${result.message}`);

            return;
        }
        toast.notify('success', `Re-checked ${result.data.scanned} room(s).`);
        setReloadKey(k => k + 1);
    };

    return (
        <Page>
            <Header>
                <PageTitle>Alerts</PageTitle>
                <Button
                    variant="secondary"
                    iconLeft={<RefreshIcon size={15} />}
                    isDisabled={rescanning}
                    onClick={() => void recheck()}
                >
                    Re-check now
                </Button>
            </Header>

            <Card>
                <Toolbar>
                    <Muted style={{ fontSize: 13 }}>Tenant: {tenant}</Muted>
                    <Check>
                        <input
                            type="checkbox"
                            checked={includeAck}
                            onChange={e => setIncludeAck(e.target.checked)}
                        />
                        Show acknowledged
                    </Check>
                </Toolbar>

                {error ? (
                    <div style={{ padding: 16 }}>
                        <Notice kind="warning">Couldn&apos;t load alerts: {error}</Notice>
                        <div style={{ marginTop: 12 }}>
                            <Button variant="secondary" onClick={() => setReloadKey(k => k + 1)}>Retry</Button>
                        </div>
                    </div>
                ) : alerts === null ? (
                    <SkeletonRows rows={3} />
                ) : alerts.length === 0 ? (
                    <EmptyState
                        icon={<CheckCircleSolidIcon size={26} />}
                        heading={includeAck ? 'No alerts' : 'All clear'}
                        body={includeAck
                            ? 'No active or acknowledged alerts for this tenant.'
                            : 'No active alerts — every monitored room is healthy.'}
                    />
                ) : (
                    alerts.map(alert => (
                        <Row key={alert.id}>
                            <SeverityTag severity={alert.severity} />
                            <Body>
                                <Msg>{alert.message}</Msg>
                                <Sub>
                                    <RoomLink onClick={() => navigate(`/rooms/${encodeURIComponent(alert.roomId)}`)}>
                                        {alert.roomId}
                                    </RoomLink>
                                    <span>raised {fmtRelative(alert.raisedAt)}</span>
                                    {alert.status === 'acknowledged' ? <span>· acknowledged</span> : null}
                                </Sub>
                            </Body>
                            {alert.status === 'acknowledged' ? (
                                <Muted style={{ fontSize: 12.5 }}>Acknowledged</Muted>
                            ) : (
                                <Button
                                    variant="secondary"
                                    isDisabled={busy === alert.id}
                                    iconLeft={<CheckCircleIcon size={14} />}
                                    onClick={() => void acknowledge(alert)}
                                >
                                    Acknowledge
                                </Button>
                            )}
                        </Row>
                    ))
                )}
            </Card>
        </Page>
    );
}
